import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { cognitoLogin, CURRENT_USER, OWNER, resetState, stateIsLoading } from '@guryou/redux';
import logo from 'assets/img/logo.png';
import { persistor } from 'store';
import { useFeelPanelBranding } from 'components/FeelPanelBrandingContext';
import { clearWidgetAuthIntegrationContext } from 'constants/widgetIntegrationBranding';
import { authInputFocusProps } from './authInputFocusProps';

const Login = ({ setView, hideRegister = false, hideForgotPassword = false, branding, isWidgetAuthFlow = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { setPanelBranding } = useFeelPanelBranding();
  const auth = useSelector(state => state.auth);
  const accent = branding?.accentColor ?? 'var(--feel-primary)';
  const logoSrc = branding?.logoUrl ? branding.logoUrl : logo;

  // Create Zod schema with translations
  const loginSchema = z.object({
    email: z
      .string()
      .min(1, t('LOGIN_MODAL.validation.emailRequired'))
      .email(t('LOGIN_MODAL.validation.emailInvalid')),
    password: z
      .string()
      .min(1, t('LOGIN_MODAL.validation.passwordRequired'))
      .min(6, t('LOGIN_MODAL.validation.passwordMinLength')),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(loginSchema),
    mode: 'onBlur',
  });

  useEffect(() => {
    dispatch(resetState(CURRENT_USER));
    dispatch(resetState(OWNER));
  }, [dispatch]);

  const onSubmit = data => {
    if (!isWidgetAuthFlow) {
      clearWidgetAuthIntegrationContext();
      setPanelBranding(null);
    }
    const { email, password } = data;
    dispatch(cognitoLogin({ email, password, persistor }));
  };

  if (stateIsLoading(auth)) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: accent }}></div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Modern Card Container */}
      <div className="rounded-2xl overflow-hidden">
        {/* Header with Logo */}
        <div className="text-center border-b border-gray-100 pb-6">
          <div className="flex justify-center mb-4">
            <img src={logoSrc} alt={branding?.brandName || 'Logo'} className="h-12 w-auto max-w-[200px] object-contain" />
          </div>
          {branding?.brandName ? <p className="text-sm font-medium text-gray-800 mb-1">{branding.brandName}</p> : null}
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('LOGIN_MODAL.welcomeBack')}</h2>
          <p className="text-sm text-gray-600">{t('LOGIN_MODAL.signInToYourAccount')}</p>
        </div>

        {/* Form Content */}
        <div className="space-y-6 p-2 mt-2">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('GENERAL.email')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                    />
                  </svg>
                </div>
                <input
                  type="email"
                  id="email"
                  autoComplete="username"
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm placeholder-gray-400 focus:outline-none transition-all duration-200 ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  }`}
                  {...authInputFocusProps(accent, !!errors.email, register('email'))}
                  placeholder={t('LOGIN_MODAL.enterYourEmail')}
                />
              </div>
              {errors.email && (
                <p className="mt-2 text-sm text-red-600 flex items-center" role="alert">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                {t('GENERAL.password')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <input
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  className={`w-full pl-10 pr-4 py-3 border rounded-xl shadow-sm placeholder-gray-400 focus:outline-none transition-all duration-200 ${
                    errors.password ? 'border-red-300' : 'border-gray-300'
                  }`}
                  {...authInputFocusProps(accent, !!errors.password, register('password'))}
                  placeholder={t('LOGIN_MODAL.enterYourPassword')}
                />
              </div>
              {errors.password && (
                <p className="mt-2 text-sm text-red-600 flex items-center" role="alert">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Forgot Password Link */}
            {!hideForgotPassword && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setView('passwordForgoten')}
                  className="text-sm font-medium transition-colors hover:underline"
                  style={{ color: accent }}
                >
                  {t('GENERAL.dontKnowPassword')}
                </button>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex justify-center items-center py-3.5 px-4 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 hover:scale-105 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              style={{ backgroundColor: accent }}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  {t('LOGIN_MODAL.loggingIn')}
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                    />
                  </svg>
                  {t('GENERAL.login')}
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer with Register Link */}
        {!hideRegister && (
          <div className="py-6 bg-gray-50 border-t border-gray-100">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-3">{t('LOGIN_MODAL.dontHaveAnAccount')}</p>
              <button
                type="button"
                onClick={() => setView('register')}
                className="inline-flex items-center text-sm font-semibold transition-colors hover:underline"
                style={{ color: accent }}
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                  />
                </svg>
                {t('GENERAL.dontHaveAnAccountRegister')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default withRouter(Login);
